import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyOpportunityAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, Plus, Briefcase, MapPin, Clock,
  Users, ChevronRight, Loader2, IndianRupee, XCircle
} from 'lucide-react';

const STATUS_CONFIG = {
  OPEN:   { label: 'Open',   color: 'bg-green-100 text-green-700'  },
  DRAFT:  { label: 'Draft',  color: 'bg-gray-100 text-gray-500'    },
  PAUSED: { label: 'Paused', color: 'bg-yellow-100 text-yellow-700'},
  FILLED: { label: 'Filled', color: 'bg-blue-100 text-blue-700'    },
  CLOSED: { label: 'Closed', color: 'bg-red-100 text-red-600'      },
};

export default function MyPostedOpportunities() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await companyOpportunityAPI.list();
      setOpportunities(res.data.data || []);
    } catch { toast.error('Failed to load opportunities'); }
    finally { setLoading(false); }
  };

  const handleClose = async (id) => {
    if (!window.confirm('Close this opportunity? It will no longer accept applications.')) return;
    try {
      await companyOpportunityAPI.close(id);
      toast.success('Opportunity closed');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to close'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/dashboard')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold uppercase">Company</span>
          </div>
          <button onClick={() => { logout(); navigate('/company/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Posted Opportunities</h1>
            <p className="text-sm text-gray-400 mt-0.5">Manage your open job postings</p>
          </div>
          <button onClick={() => navigate('/company/opportunities/post')}
            className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-xl
              font-bold text-sm hover:bg-teal-700 transition shadow-lg shadow-teal-500/20">
            <Plus className="h-4 w-4" /> Post Opportunity
          </button>
        </div>

        {opportunities.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <Briefcase className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <h3 className="font-bold text-gray-400 text-lg">No opportunities posted yet</h3>
            <p className="text-sm text-gray-400 mt-1 mb-6">
              Post an opportunity to let verified staff apply directly
            </p>
            <button onClick={() => navigate('/company/opportunities/post')}
              className="bg-teal-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-teal-700 transition">
              Post First Opportunity
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {opportunities.map(opp => {
              const cfg = STATUS_CONFIG[opp.status] || STATUS_CONFIG.OPEN;
              return (
                <div key={opp.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-bold text-slate-800">{opp.title}</h3>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}>
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-sm text-teal-600 font-medium">{opp.roleName}</p>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-gray-500">
                        {opp.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{opp.city}{opp.state ? `, ${opp.state}` : ''}</span>}
                        {opp.shiftHours && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{opp.shiftHours}hr shift</span>}
                        {(opp.dailyRateMin || opp.dailyRateMax) && (
                          <span className="flex items-center gap-1">
                            <IndianRupee className="h-3 w-3" />
                            {opp.dailyRateMin && `₹${opp.dailyRateMin}`}
                            {opp.dailyRateMin && opp.dailyRateMax && ' – '}
                            {opp.dailyRateMax && `₹${opp.dailyRateMax}`}/day
                          </span>
                        )}
                        {opp.applicationCount != null && (
                          <span className="flex items-center gap-1"><Users className="h-3 w-3" />{opp.applicationCount} applicant{opp.applicationCount !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {opp.status === 'OPEN' && opp.applicationCount > 0 && (
                        <button onClick={() => navigate(`/company/opportunities/${opp.id}/applicants`)}
                          className="flex items-center gap-1.5 bg-teal-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-teal-700 transition">
                          View Applicants <ChevronRight className="h-3 w-3" />
                        </button>
                      )}
                      {opp.status === 'OPEN' && (
                        <button onClick={() => handleClose(opp.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition">
                          <XCircle className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {opp.requiredFrom && (
                    <p className="text-xs text-gray-400 mt-3">
                      Required from {new Date(opp.requiredFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {opp.requiredUntil && ` to ${new Date(opp.requiredUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
